import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import WithTooltip from "../components/WithTooltip";
import {
  HiOutlinePlus,
  HiOutlineExclamation,
  HiOutlineSearch,
  HiOutlineDocumentSearch,
  HiOutlineInformationCircle,
  HiPlus,
  HiChevronDown,
  HiChevronUp,
  HiOutlineQuestionMarkCircle,
  HiChevronRight,
  HiChevronLeft,
} from "react-icons/hi";
import type { NextPage } from "next";
import Head from "next/head";

import ReactDOM from "react-dom";

import { useSegmentCallback } from "../src/segment";

import Button from "../components/Button";
import FactView from "../components/FactView";
import InlineCode from "../components/InlineCode";
import LoadingPage from "../components/LoadingPage";
import Modal from "../components/Modal";
import { WithSidebar } from "../components/Sidebar";
import Help from "../components/Help";
import { AutocompleteInfo, Fact, TypedId, EnvData } from "../src/types";

import useEnv from "../src/useEnv";
import useData from "../src/useData";
import { useRouter } from "next/router";
import { useEnvironmentAction } from "../src/useAction";
import { Tab, TabGroup } from "../components/Tab";
import Link from "next/link";
import { Policy } from "../components/Policy";
import { BACKGROUND_COLOR as POLICY_BACKGROUND_COLOR } from "../components/Policy";
import ReactFocusLock from "react-focus-lock";
import useOutsideClick from "../src/useOutsideClick";
import { useTransition } from "transition-hook";
import WithIcon from "../components/WithIcon";
import GetStartedCta from "../components/GetStartedCta";
import MainContent from "@/components/MainContent";

export const FactRow: FunctionComponent<{
  fact: Fact;
  refresh: () => void;
  setSearch?: (search: string) => void;
}> = ({ fact, refresh, setSearch }) => {
  const { selectedEnv } = useEnv();
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteFactAction = useEnvironmentAction("delete_fact");

  const deleteFact = useCallback(() => {
    setIsDeleting(true);
    // TODO: would be nice to be able to use the official Oso JS SDK for these
    const doDelete = async () => {
      if (!selectedEnv) return [];
      try {
        const resp = await fetch(
          `/web/environment/${selectedEnv.pub_id}/api/facts`,
          {
            method: "DELETE",
            body: JSON.stringify(fact),
          }
        );
        return await resp.json();
      } catch (e) {
        console.error(`Error while fetching facts: ${e}`);
        return {};
      }
    };

    doDelete().then(() => refresh());
  }, [fact, refresh, selectedEnv]);

  return (
    <div className="flex items-center justify-between w-full mb-1 group hover:bg-gray-50">
      <FactView
        legacy
        fact={fact}
        onArgClick={(arg: TypedId) =>
          setSearch && setSearch(`${arg.type}:${arg.id}`)
        }
      />
      <button
        role="button"
        disabled={isDeleting || !deleteFactAction}
        className="border border-red-800 rounded text-sm px-2 py-0.5 text-red-800 invisible group-hover:visible disabled:opacity-40 disabled:visible"
        onClick={deleteFact}
      >
        {isDeleting ? "Deleting..." : "Delete"}
      </button>
    </div>
  );
};

const ViewFacts: React.FC = () => {
  const [filterString, setFilterString] = useState("");
  const { selectedEnv } = useEnv();

  // For keeping around the last search result when performing a new search.
  const [cachedFacts, setCachedFacts] = useState<Fact[]>([]);

  // Fetch filterId and filterType from filterString
  const filterStringParsed: string[] = filterString.split(":");
  let filterType = "";
  let filterId = "";
  let filterError = false;
  if (filterStringParsed.length === 2) {
    [filterType, filterId] = filterStringParsed;
  }
  if (filterString && (!filterType || !filterId)) {
    // User typed a filter string but it's not able to be parsed into a type and ID
    filterError = true;
  }
  // Support wildcards
  if (filterId === "_") filterId = "";
  if (filterType === "_") filterType = "";

  const hasFilter = !!filterId || !!filterType;

  const path = useMemo(() => {
    if (!selectedEnv) return null;
    const shouldInspect = (filterId || filterType) && !filterError;
    return shouldInspect
      ? `/web/environment/${selectedEnv.pub_id}/api/inspect?${
          filterType && `query.type=${filterType}`
        }&${filterId && `query.id=${filterId}&limit=200`}`
      : `/web/environment/${selectedEnv.pub_id}/api/facts?limit=200`;
  }, [filterId, filterType, filterError, selectedEnv]);

  const { data, refresh } = useData<Fact[]>(() => path);

  // Update cachedFacts when facts are successfully fetched
  useEffect(() => {
    if (data) setCachedFacts(data);
  }, [data]);

  // Refresh every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => refresh(), 5000);
    return () => clearInterval(interval);
  }, [refresh]);

  const facts = data || cachedFacts;

  if (!facts) {
    return <LoadingPage />;
  }

  return (
    <>
      <h2 className="text-2xl font-space">Browse all facts</h2>
      <div className="mt-2" />
      <div>
        Your environment contains the following facts, which Oso uses to make
        authorization decisions for you.
      </div>
      <div className="mt-6" />
      <div className="flex flex-wrap space-x-3 max-w-[1000px] items-baseline">
        {!hasFilter && facts.length === 0 ? (
          <div className="text-gray-600 grow">
            Your environment doesn{"'"}t contain any facts.
            <GetStartedCta />
          </div>
        ) : (
          <div className="flex items-center space-x-3">
            <div
              className={`border px-2 py-1 text-base min-w-[350px] rounded
                  flex space-x-3 items-center
                  ${filterError && "border-red-700"}`}
            >
              <HiOutlineSearch className="shrink-0" />
              <input
                className="outline-none w-full grow appearance-none"
                type="text"
                value={filterString}
                onChange={(e) => setFilterString(e.target.value)}
                placeholder="Filter facts..."
              />
              {filterString && (
                <Button xsmall onClick={() => setFilterString("")}>
                  Clear
                </Button>
              )}
            </div>
            <Help>
              Enter a Type and ID combination, like{" "}
              <InlineCode>User:bob</InlineCode>.
            </Help>
          </div>
        )}
      </div>

      <div className="mt-6" />

      {facts.length === 0 && hasFilter && (
        <div className="text-gray-600">
          Your environment doesn{"'"}t contain any facts matching{" "}
          <InlineCode>{filterString}</InlineCode>.
        </div>
      )}
      <div className="max-w-[1000px]">
        {facts.map((fact) => (
          <FactRow
            // TODO: this is probably an extremely inefficient way of keying facts
            // We should eventually have a unique fact ID, or memoize this somehow
            key={JSON.stringify(fact)}
            fact={fact}
            refresh={refresh}
            setSearch={setFilterString}
          />
        ))}
        {facts.length == 200 && (
          <div className="text-gray-600">Showing a maximum of 200 facts.</div>
        )}
      </div>
    </>
  );
};

export interface FactType {
  fact_type: Fact;
  locations: [SourceLocation, SourceLocation][];
}

interface SourceLocation {
  row: number;
  col: number;
}

export const FactSchema: React.FC<{ selectedEnv: EnvData | null }> = ({
  selectedEnv,
}) => {
  const [viewSourceType, setViewSourceType] = useState<FactType | null>(null);
  const [addFactType, setAddFactType] = useState<FactType | null>(null);
  const [browseType, setBrowseType] = useState<FactType | null>(null);
  const [filterString, setFilterString] = useState<string>("");
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const canCreateFact = useEnvironmentAction("create_fact");

  const getAddFactButtonTooltip = () => {
    if (canCreateFact || !selectedEnv) return null;

    if (selectedEnv.protected) {
      return "Only admins can add facts in protected environments.";
    }

    // There's no existing case for this, but it's a good fallback in case
    // the policy evolves and there's a new reason the user doesn't have
    // permissions to add facts.
    return "You need additional permissions to add facts.";
  };

  const { data: policyObj, isLoading: policyLoading } = useData<{
    policy: { src: string };
  }>(() => {
    if (selectedEnv === null) {
      return null;
    }
    return `/web/environment/${selectedEnv.pub_id}/api/policy/`;
  });

  const { data: factTypesResponse, isLoading: factTypesLoading } = useData<{
    fact_types: FactType[];
  }>(() => {
    if (selectedEnv === null) {
      return null;
    }
    return `/web/environment/${selectedEnv.pub_id}/api/fact_types/`;
  });

  const {
    data: browseFacts,
    refresh: refreshBrowseFacts,
    isLoading: browseFactsLoading,
  } = useData<Fact[] | null>(() => {
    if (!selectedEnv || !browseType) {
      return;
    }
    return `/web/environment/${
      selectedEnv.pub_id
    }/api/facts_of_type/?limit=200&predicate=${encodeURIComponent(
      browseType.fact_type.predicate
    )}&${browseType.fact_type.args
      .map((a, i) => {
        if (a.type !== "_" && a.id !== "_") {
          return `args.${i}.id=${encodeURIComponent(
            a.id
          )}&args.${i}.type=${encodeURIComponent(a.type)}`;
        } else if (a.type !== "_") {
          return `args.${i}.type=${encodeURIComponent(a.type)}`;
        } else {
          return `args.${i}=`;
        }
      })
      .join("&")}`;
  });

  const deleteFact = useCallback(
    (fact: Fact, refresh: () => void, selectedEnv: EnvData) => {
      setIsDeleting(true);
      const doDelete = async () => {
        try {
          const resp = await fetch(
            `/web/environment/${selectedEnv.pub_id}/api/facts`,
            {
              method: "DELETE",
              body: JSON.stringify(fact),
            }
          );
          return await resp.json();
        } catch (e) {
          console.error(`Error while deleting fact: ${e}`);
          return {};
        } finally {
          setIsDeleting(false);
        }
      };

      doDelete().then(() => refresh());
    },
    []
  );

  const argToFilterString = useCallback((arg: TypedId) => {
    if (arg.type === "_" && arg.id === "_") {
      return "_"; // Any
    } else if (arg.type == "String" && arg.id !== "_") {
      return `"${arg.id}"`; // String literal
    } else {
      return arg.type;
    }
  }, []);

  const factTypesEqual = useCallback((f1: FactType, f2: FactType): boolean => {
    return (
      f1.fact_type.predicate === f2.fact_type.predicate &&
      f1.fact_type.args.length === f2.fact_type.args.length &&
      f1.fact_type.args.every((a1, i) => {
        const a2 = f2.fact_type.args[i];
        return a1.type === a2.type && a1.id === a2.id;
      })
    );
  }, []);

  const filteredFactTypes = useMemo(() => {
    const factTypes = factTypesResponse?.fact_types ?? [];
    const filterStringParts = filterString.split(/\s+/);

    return filterString
      ? factTypes.filter((factType) => {
          return filterStringParts.every((filterItem, idx) => {
            if (filterItem === "") {
              return true;
            }
            // Require an exact match for every token *except* the last one,
            // which we'll use as a prefix
            if (idx === filterStringParts.length - 1) {
              return (
                factType.fact_type.predicate.startsWith(filterItem) ||
                factType.fact_type.args.some((arg) =>
                  argToFilterString(arg).startsWith(filterItem)
                )
              );
            } else {
              return (
                factType.fact_type.predicate === filterItem ||
                factType.fact_type.args.some(
                  (arg) => argToFilterString(arg) === filterItem
                )
              );
            }
          });
        })
      : factTypes;
  }, [filterString, factTypesResponse, argToFilterString]);

  const isDefaultHasPermission = useCallback((factType: Fact) => {
    // We bake `has_permission(_, _, _)` into policies as part of the
    // default allow rule- doesn't make sense to highlight it in the
    // policy.
    return (
      factType.predicate == "has_permission" &&
      factType.args.length == 3 &&
      factType.args.every((arg) => arg.type === "_" && arg.id === "_")
    );
  }, []);

  // INVARIANT: highlightedLines is sorted numerically in ascending order
  const highlightedLines: number[] = useMemo(() => {
    const highlightedLines: number[] = [];
    if (viewSourceType && !isDefaultHasPermission(viewSourceType.fact_type)) {
      viewSourceType.locations.forEach((location) => {
        for (let l = location[0].row; l <= location[1].row; l++) {
          highlightedLines.push(l);
        }
      });
    }
    highlightedLines.sort((a, b) => a - b); // Otherwise it'll sort them lexicographically
    return highlightedLines;
  }, [viewSourceType, isDefaultHasPermission]);

  // Produces a string like "Used on lines 14-16, 20, 28-44"
  // (depends on highlightedLines being sorted!)
  const highlightedLinesDescription: string = useMemo(() => {
    if (highlightedLines.length === 0) {
      return "";
    }
    let descriptionGroups: string[] = [];
    let currentGroup: number[] = [];

    const pushGroup = () => {
      if (currentGroup.length > 1) {
        const firstLine = currentGroup[0];
        const lastLine = currentGroup[currentGroup.length - 1];
        descriptionGroups.push(`${firstLine + 1}-${lastLine + 1}`);
      } else if (currentGroup.length === 1) {
        const singleLine = currentGroup[0];
        descriptionGroups.push(`${singleLine + 1}`);
      }
    };
    highlightedLines.forEach((lineNumber) => {
      // Group these into contiguous line numbers.
      if (
        currentGroup.length > 0 &&
        currentGroup[currentGroup.length - 1] !== lineNumber - 1
      ) {
        pushGroup();
        currentGroup = [];
      }
      currentGroup.push(lineNumber);
    });
    pushGroup();

    return (
      "Used on " +
      (highlightedLines.length === 1 ? "line " : "lines ") +
      descriptionGroups.join(", ")
    );
  }, [highlightedLines]);

  const drawerOpen = viewSourceType !== null;
  const policy: string = policyObj?.policy?.src ?? "";

  if (!selectedEnv || policyLoading || factTypesLoading) {
    return <LoadingPage />;
  }

  if (!policy) {
    return (
      <div
        className="text-gray-600
    "
      >
        {
          "You don't have a policy yet, so we can't show you the facts your policy uses."
        }
        <GetStartedCta />
      </div>
    );
  }
  return (
    <div>
      <h2 className="text-2xl font-space">{"Your policy's fact types"}</h2>
      <div className="mt-2" />
      <div>
        Your policy is backed by concrete authorization-relevant data called{" "}
        <a
          href="https://www.osohq.com/docs/concepts/oso-cloud-data-model"
          target="_blank"
          rel="noreferrer"
          className="underline text-blue-600"
        >
          facts
        </a>
        .
      </div>
      <div>
        Based on your policy, you can add facts of any of the following types to
        power authorization decisions.
      </div>
      <div className="mt-6" />
      <div className="flex items-center space-x-3">
        <div
          className="border px-2 py-1 text-base min-w-[350px] rounded
                  flex space-x-3 items-center"
        >
          <HiOutlineSearch className="shrink-0" />
          <input
            className="outline-none w-full grow appearance-none"
            type="text"
            value={filterString}
            onChange={(e) => {
              setFilterString(e.target.value);
              setViewSourceType(null);
              setBrowseType(null);
            }}
            placeholder="Filter fact types..."
          />
          {filterString && (
            <Button
              xsmall
              onClick={() => {
                setFilterString("");
                setViewSourceType(null);
              }}
            >
              Clear
            </Button>
          )}
        </div>
        <Help>
          Enter a type or a fact name, or click on one of the fact names or
          argument types below.
        </Help>
      </div>
      <div className="mt-4" />
      {filteredFactTypes.length === 0 &&
        (filterString ? (
          <div>
            {"Your policy doesn't use any facts matching "}
            <code className="text-xs border rounded border-gray-300 bg-gray-100 text-oso-fact-red px-1 py-0.5">
              {filterString}
            </code>
            .
          </div>
        ) : (
          <div>{"Your policy doesn't use any facts at all."}</div>
        ))}
      <ul>
        {filteredFactTypes.map((t, i) => {
          if (isDefaultHasPermission(t.fact_type)) {
            return <></>;
          }
          return (
            <li key={`fact-type-${i}`}>
              <div
                className={`flex flex-row space-x-2 items-center mt-2 ${
                  viewSourceType == t
                    ? "bg-[rgb(219,_255,_219)]" // diff green
                    : browseType === null
                    ? "hover:bg-gray-50"
                    : ""
                } ${
                  browseType === null || factTypesEqual(browseType, t)
                    ? "opacity-100"
                    : "opacity-50 hover:opacity-100"
                }`}
              >
                <FactView
                  legacy
                  fact={t.fact_type}
                  typesOnly
                  onPredicateClick={() => {
                    setFilterString(
                      (filterString ? filterString + " " : "") +
                        t.fact_type.predicate +
                        " "
                    );
                    setViewSourceType(null);
                    setBrowseType(null);
                  }}
                  onArgClick={(arg) => {
                    setFilterString(
                      (filterString ? filterString + " " : "") +
                        argToFilterString(arg) +
                        " "
                    );
                    setViewSourceType(null);
                    setBrowseType(null);
                  }}
                />
                <div className="flex-grow" />
                <WithTooltip tooltip={getAddFactButtonTooltip()}>
                  <Button
                    small
                    theme="secondary"
                    icon={<HiOutlinePlus />}
                    onClick={() => {
                      setAddFactType(t);
                    }}
                    disabled={!canCreateFact}
                  >
                    Add
                  </Button>
                </WithTooltip>
                {browseType && factTypesEqual(browseType, t) ? (
                  <Button
                    small
                    theme="secondary"
                    icon={<HiChevronUp />}
                    onClick={() => {
                      setBrowseType(null);
                    }}
                  >
                    {/* set an explicit width (16 "0"-characters wide) to prevent jumpiness */}
                    <span className="w-[16ch]">Hide matching facts</span>
                  </Button>
                ) : (
                  <Button
                    small
                    theme="secondary"
                    icon={<HiChevronDown />}
                    onClick={() => {
                      setBrowseType(t);
                    }}
                  >
                    {/* set an explicit width (16 "0"-characters wide) to prevent jumpiness */}
                    <span className="w-[16ch]">Show matching facts</span>
                  </Button>
                )}
                <Button
                  small
                  theme="secondary"
                  icon={<HiOutlineDocumentSearch />}
                  onClick={() => {
                    setViewSourceType(t);
                  }}
                >
                  Find in policy
                </Button>
              </div>
              {browseType && factTypesEqual(browseType, t) && (
                <div>
                  <div className="my-2 p-4 border border-gray-200">
                    {browseFactsLoading ? (
                      <LoadingPage />
                    ) : browseFacts && browseFacts.length > 0 ? (
                      <>
                        <div>
                          {browseFacts.length >= 200 ? (
                            <>
                              {
                                "These are the first 200 facts of this type that you've added."
                              }
                            </>
                          ) : (
                            <>
                              {
                                "These are all the facts of this type that you've added."
                              }
                            </>
                          )}{" "}
                          Oso uses these as inputs to your policy to make
                          authorization decisions.
                        </div>
                        <div className="mt-2" />
                        {/* TODO(killpack) should probably merge this with FactView */}
                        <table className="text-sm border-separate border-spacing-y-1">
                          <tbody>
                            {browseFacts.map((f, i) => (
                              <tr
                                key={`fact-${i}`}
                                className="group hover:bg-gray-50"
                              >
                                <td className="text-gray-400 group-hover:text-inherit font-mono whitespace-nowrap">
                                  {t.fact_type.predicate} (&nbsp;
                                </td>
                                {f.args.map((arg, argI) => (
                                  <td
                                    key={`arg-${i}-${argI}`}
                                    className="font-mono whitespace-nowrap"
                                  >
                                    {arg.type === "String" ? (
                                      <span>{`"${arg.id}"`}</span>
                                    ) : (
                                      <>
                                        <span className="text-indigo-800">
                                          {arg.type}
                                          {"{"}
                                        </span>
                                        <span>
                                          {["Boolean", "Integer"].includes(
                                            arg.type
                                          )
                                            ? arg.id
                                            : `"${arg.id}"`}
                                        </span>
                                        <span className="text-indigo-800">
                                          {"}"}
                                        </span>
                                      </>
                                    )}
                                    <span className="text-gray-400 group-hover:text-inherit ">
                                      {argI < f.args.length - 1 ? " , " : " )"}
                                    </span>
                                  </td>
                                ))}
                                <td className="w-full" />
                                <td className="whitespace-nowrap">
                                  <Button
                                    xsmall
                                    theme="delete"
                                    disabled={isDeleting}
                                    onClick={() =>
                                      // TODO(killpack) confirm deletes
                                      deleteFact(
                                        f,
                                        refreshBrowseFacts,
                                        selectedEnv
                                      )
                                    }
                                  >
                                    Delete
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    ) : (
                      <>
                        {/* TODO(killpack)- Ideally we'd express something like "there are no facts
                         * of this type because either you need to add a fact OR it is an
                         * implied fact that doesn't need to be concretely assigned" but it's hard
                         * to do that in a way that's not more confusing
                         */}
                        <div>
                          {
                            "Your environment doesn't contain any facts of this type."
                          }
                        </div>
                        {t.fact_type.predicate === "has_role" && (
                          <>
                            <div className="mt-2" />
                            <div>
                              Oso needs to know about the role assignments in
                              your application.
                            </div>
                          </>
                        )}
                        {t.fact_type.predicate === "has_relation" && (
                          <>
                            <div className="mt-2" />
                            <div>
                              Oso needs to know about the relationships between
                              instances of your application{"'"}s entities.
                            </div>
                          </>
                        )}
                        {t.fact_type.predicate === "has_permission" && (
                          <>
                            <div className="mt-2" />
                            <div>
                              This is a good thing&mdash; it{"'"}s best if
                              permissions are granted from rules in your policy,
                              rather than being granted directly via{" "}
                              <code className="text-sm">has_permission</code>{" "}
                              facts.
                            </div>
                          </>
                        )}
                        <div className="mt-2" />
                        <div>
                          <WithIcon icon={<HiOutlineInformationCircle />}>
                            To learn how your policy uses facts of this type,
                            click{" "}
                            <Button
                              xsmall
                              theme="secondary"
                              icon={<HiOutlineDocumentSearch />}
                              onClick={() => {
                                setViewSourceType(t);
                              }}
                            >
                              Find in policy
                            </Button>
                            {". "}
                            To add a fact of this type to Oso, click{" "}
                            <WithTooltip tooltip={getAddFactButtonTooltip()}>
                              <Button
                                xsmall
                                theme="secondary"
                                icon={<HiPlus />}
                                onClick={() => {
                                  setAddFactType(t);
                                }}
                                disabled={!canCreateFact}
                              >
                                Add
                              </Button>
                            </WithTooltip>
                            {". "}
                          </WithIcon>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="mt-6" />
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="mt-8" />

      <Modal
        isOpen={addFactType !== null}
        onClose={() => setAddFactType(null)}
        showCloseButton={true}
      >
        {addFactType && (
          <AddFactOfType
            factType={addFactType}
            selectedEnv={selectedEnv}
            onAdd={() => {
              setAddFactType(null);
              setBrowseType(addFactType);
              refreshBrowseFacts();
            }}
          />
        )}
      </Modal>
      <Drawer
        isOpen={drawerOpen}
        onClose={() => setViewSourceType(null)}
        afterOpen={() => {}}
      >
        <div className={`bg-[${POLICY_BACKGROUND_COLOR}] p-2 pt-0 min-h-full`}>
          <div
            className={`flex sticky top-0 bg-[${POLICY_BACKGROUND_COLOR}] pt-2`}
          >
            <div className="flex-grow" />
            <div className="text-sm">{highlightedLinesDescription}</div>
          </div>
          <Policy
            policy={policy}
            lines={highlightedLines}
            scrollToLine={
              highlightedLines.length > 0 ? highlightedLines[0] : undefined
            }
          />
        </div>
      </Drawer>
    </div>
  );
};

export const AddFactOfType: React.FC<{
  factType: FactType;
  selectedEnv: EnvData;
  onAdd: () => void;
  inline?: boolean;
}> = ({ factType, selectedEnv, onAdd, inline }) => {
  const [fact, setFact] = useState<Fact>(factType.fact_type);
  const [error, setError] = useState<string | null>(null);
  const [savingFact, setSavingFact] = useState<boolean>(false);

  const segmentCallback = useSegmentCallback("Fact Schema Add Form Submit");

  const factIsComplete = fact.args.every(
    (arg) =>
      arg.type !== "_" && arg.id !== "_" && arg.type !== "" && arg.id !== ""
  );

  const addFact = useCallback(
    async (e) => {
      e.preventDefault();
      if (!factIsComplete) {
        return;
      }
      segmentCallback();
      try {
        setError(null);
        setSavingFact(true);

        const resp = await fetch(
          `/web/environment/${selectedEnv.pub_id}/api/facts/`,
          {
            method: "POST",
            body: JSON.stringify(fact),
          }
        );
        if (!resp.ok) {
          if (resp.status === 400) {
            let body = await resp.json();
            throw (
              "Couldn't add this fact because it is invalid:\n" + body.message
            );
          } else {
            throw "There was a problem adding this fact. Please try again later.";
          }
        }

        onAdd();
      } catch (e) {
        // In practice we expect this to be pretty rare, and most likely a screw-up on our part.
        console.error(`Error adding typed fact: ${e}`);
        setError(`${e}`);
      } finally {
        setSavingFact(false);
      }
    },
    [fact, factIsComplete, selectedEnv, onAdd, segmentCallback]
  );

  // Used to determine the subtypes for a type.
  const { data: autocompleteInfo } = useData<AutocompleteInfo>(
    () =>
      selectedEnv &&
      `/web/environment/${selectedEnv.pub_id}/api/autocomplete_info/`
  );

  const argComponents = useMemo(() => {
    if (!autocompleteInfo) {
      return [];
    }

    // First, let's figure out the subtypes for each type.
    // TODO do this on the backend

    // The backend gives us subtype => parent types, but we want the inverse.
    const typesToImmediateSubtypes: Record<string, Set<string>> = {};
    for (const [subtype, parentTypes] of Object.entries(
      autocompleteInfo.extends
    )) {
      parentTypes.forEach((parentType) => {
        if (typesToImmediateSubtypes[parentType] === undefined) {
          typesToImmediateSubtypes[parentType] = new Set();
        }
        typesToImmediateSubtypes[parentType].add(subtype);
      });
    }

    // Returns all of the subtypes of the given type, even if there are multiple "extends" layers
    const resolveSubtypes = (parentType: string): Set<string> => {
      const allSubtypes = new Set<string>();

      const toBeResolved = new Set(typesToImmediateSubtypes[parentType] ?? []);
      while (toBeResolved.size > 0) {
        // Pop a type off the queue
        const subtype = toBeResolved[Symbol.iterator]().next().value!;
        toBeResolved.delete(subtype);

        // Add it to the set of known subtypes
        allSubtypes.add(subtype);

        // Add its subtypes, if any, to the queue
        (typesToImmediateSubtypes[subtype] || []).forEach((subsubtype) => {
          toBeResolved.add(subsubtype);
        });
      }
      return allSubtypes;
    };

    const actors = Array.from(resolveSubtypes("Actor"));
    const resources = Array.from(resolveSubtypes("Resource")).filter(
      (e) => e !== "Actor" // Actor is an abstract type, so we don't want to show it in the dropdown.
    );
    const builtins = ["String", "Boolean", "Integer"];

    return factType.fact_type.args.map((arg, i) => {
      if (arg.id !== "_") {
        // This is a literal type- nothing to input. Literals are always strings.
        return (
          <span
            key={`arg-literal-${i}`}
            className="text-indigo-800"
          >{`"${arg.id}"`}</span>
        );
      }

      const validTypes =
        arg.type === "Actor"
          ? actors
          : arg.type === "Resource"
          ? resources
          : arg.type === "_" // Wildcards can be actors, resources, or builtin types.
          ? Array.from(new Set([...actors, ...resources, ...builtins]))
          : [arg.type, ...Array.from(resolveSubtypes(arg.type))]; // Every other type can accept the type itself or its subtypes.
      validTypes.sort();
      return (
        <ArgumentInput
          key={`arg-input-${i}`}
          types={validTypes}
          parentType={arg.type}
          inline={inline}
          onChange={(arg) => {
            // Convoluted but idiomatic-React way to update the i'th arg
            setFact((oldFact) => ({
              ...oldFact,
              args: oldFact.args.map((oldArg, oldArgIndex) =>
                i === oldArgIndex ? arg : oldArg
              ),
            }));
          }}
        />
      );
    });
  }, [autocompleteInfo, factType, inline]);

  if (!autocompleteInfo) {
    return <LoadingPage />;
  }

  return (
    <>
      <h1 className="text-xl font-space">
        Add a{" "}
        <code className="p-1 break-words bg-gray-100 text-oso-fact-red">
          {factType.fact_type.predicate}
        </code>{" "}
        fact
      </h1>
      <div className="mt-2" />
      <div className="text-sm">
        <p>
          {
            "Fill in the blanks with information about your application's entities."
          }
        </p>
        <p>Oso will use this fact to make authorization decisions for you.</p>
      </div>
      <div className="mt-6" />
      <form onSubmit={addFact}>
        <div className="font-mono flex flex-col space-y-1">
          <div>
            <span className="text-gray-800">
              {factType.fact_type.predicate}
            </span>
            <span className="text-gray-400"> ( </span>
          </div>
          <div className={`flex ${inline ? "flex-row" : "flex-col"}`}>
            {argComponents.map((argComponent, i) => (
              <div key={`arg-${i}`} className={`flex ml-4`}>
                {argComponent}
                {i < factType.fact_type.args.length - 1 && (
                  <span className="text-gray-400"> , </span>
                )}
              </div>
            ))}
          </div>
          <span className="text-gray-400"> )</span>
        </div>
        <div className="mt-2" />
        <Button
          type="submit"
          medium
          theme="primary"
          disabled={!factIsComplete || savingFact}
          loading={savingFact}
        >
          Add this fact
        </Button>

        {error && (
          <div className="mt-6">
            <div className="border border-red-600 rounded px-3 py-2 text-red-600">
              <HiOutlineExclamation className="inline mr-2" />
              <span className="align-middle whitespace-pre-line">{error}</span>
            </div>
          </div>
        )}
      </form>
    </>
  );
};

// INVARIANT: types.length > 0
const ArgumentInput: React.FC<{
  types: string[];
  parentType: string;
  onChange: (arg: TypedId) => void;
  inline?: boolean;
}> = ({ types, parentType, onChange, inline }) => {
  const [arg, setArg] = useState<TypedId>({
    type: types.length === 1 ? types[0] : "",
    id: "",
  });

  useEffect(() => {
    onChange(arg);
  }, [arg, onChange]);

  return (
    <span>
      {/* Arg type: either an uneditable string, or a dropdown */}
      <span className="text-indigo-800">
        {types.length === 1 ? (
          types[0]
        ) : (
          <select
            className={`p-1 border-b bg-white ${
              arg.type === "" ? "text-gray-500" : ""
            }`}
            required
            value={arg.type}
            onChange={(e) => setArg({ id: "", type: e.target.value })}
          >
            <option value="">
              {parentType === "Actor"
                ? "Select an actor"
                : parentType === "Resource"
                ? "Select a resource"
                : "Select a type"}
            </option>
            {types.map((type, i) => (
              <option key={`type-${i}`} value={type}>
                {type}
              </option>
            ))}
          </select>
        )}
        {"{"}
      </span>
      {/* Arg value: an input that varies depending on the arg type */}
      {arg.type === "Boolean" ? (
        <select
          className="p-1 border-b bg-white"
          value={arg.id}
          onChange={(e) => setArg({ ...arg, id: e.target.value })}
          required
        >
          <option></option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      ) : arg.type === "Integer" ? (
        <input
          type="number"
          required
          autoCorrect="off"
          className="border-b"
          placeholder="123"
          value={arg.id}
          onChange={(e) => setArg({ ...arg, id: e.target.value })}
        />
      ) : arg.type !== "" ? (
        <>
          {'"'}
          <input
            required
            autoCorrect="off"
            className={`${inline && "w-25"} border-b`}
            placeholder={arg.type === "String" ? "string" : `${arg.type} ID`}
            value={arg.id}
            onChange={(e) => setArg({ ...arg, id: e.target.value })}
          />
          {'"'}
        </>
      ) : (
        <input
          required
          autoCorrect="off"
          disabled
          className={`${inline && "w-25"} border-b`}
          value={arg.id}
        />
      )}

      <span className="text-indigo-800">{"}"}</span>
    </span>
  );
};

const Drawer: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  afterOpen: () => void;
}> = ({ isOpen, onClose, afterOpen, children }) => {
  const outsideClickRef = useOutsideClick<HTMLDivElement>(onClose);

  const nodeRef = useRef<HTMLDivElement | null>(null);

  const { stage, shouldMount } = useTransition(isOpen, 300);

  useEffect(() => {
    if (stage === "enter") {
      // Disable scroll on the page body
      document.body.style.overflowY = "hidden";
    } else {
      // Re-enable scrolling
      document.body.style.overflowY = "auto";

      // We're in the middle of a transition- force a repaint to ensure CSS animations
      // work correctly
      // Adopted from https://github.com/reactjs/react-transition-group/blob/5007303e729a74be66a21c3e2205e4916821524b/src/CSSTransition.js#L208-L215
      nodeRef.current && nodeRef.current.scrollTop;
    }
  }, [stage]);

  useEffect(() => {
    const escapeKeyListener = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", escapeKeyListener);
    return () => document.removeEventListener("keydown", escapeKeyListener);
  }, [onClose]);

  useEffect(() => {
    if (stage == "enter") {
      afterOpen();
    }
  }, [afterOpen, stage]);

  return (
    <Portal>
      {shouldMount && (
        <ReactFocusLock>
          <div
            className={`transition-opacity fixed z-40 inset-0 flex-col flex items-center bg-gray-300 bg-opacity-80 overflow-hidden duration-300 ${
              stage == "enter" ? "opacity-1" : "opacity-0"
            }`}
            ref={nodeRef}
          ></div>
          <div
            className={`fixed top-0 right-0 z-50 w-1/2 h-full transition-transform transform bg-white overflow-y-auto duration-300 ${
              stage == "enter" ? "translate-x-0" : "translate-x-full"
            }`}
            ref={outsideClickRef}
          >
            {children}
          </div>
        </ReactFocusLock>
      )}
    </Portal>
  );
};

const Portal: React.FC = ({ children }) => {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = document.createElement("div");
    document.body.appendChild(node);
    setNode(node);
    return () => {
      document.body.removeChild(node);
    };
  }, [setNode]);

  if (!node) {
    return null;
  }

  return ReactDOM.createPortal(children, node);
};

const FactsPage: NextPage = () => {
  const { selectedEnv } = useEnv();

  const { query } = useRouter();

  const [helpOpen, setHelpOpen] = useState<boolean>(false);
  const [helpStep, setHelpStep] = useState<"what" | "how">("what");
  const helpOpenSegment = useSegmentCallback(
    "Facts 'What's a Fact' Modal Open"
  );

  const tabs = [
    {
      tabKey: "schema",
      title: "Fact schema",
      contents: <FactSchema selectedEnv={selectedEnv} />,
    },
    {
      tabKey: "view",
      title: "Browse all facts",
      contents: <ViewFacts />,
    },
  ];

  const tabKeys = tabs.map((t) => t.tabKey);

  const selectedTabKey =
    query.tab && tabKeys.includes(query.tab.toString())
      ? query.tab.toString()
      : tabKeys[0];

  const selectedTab = tabs.find((t) => t.tabKey === selectedTabKey);

  return (
    <>
      <Head>
        <title>Oso Cloud - Data</title>
      </Head>
      <WithSidebar>
        <MainContent>
          <div className="flex flex-row">
            <h1 className="font-space text-3xl">Data</h1>
            <div className="flex-grow" />
            <div>
              <Button
                medium
                icon={<HiOutlineQuestionMarkCircle />}
                onClick={() => {
                  helpOpenSegment();
                  setHelpStep("what");
                  setHelpOpen(true);
                }}
              >
                {"What's a fact?"}
              </Button>
            </div>
          </div>
          <TabGroup>
            {tabs.map(({ tabKey, title }) => (
              <Link href={`?tab=${tabKey}`} passHref key={tabKey}>
                <Tab title={title} isSelected={selectedTabKey === tabKey} />
              </Link>
            ))}
          </TabGroup>
          <div className="mt-8" />
          {selectedTab?.contents}
        </MainContent>

        <Modal
          isOpen={helpOpen}
          onClose={() => {
            setHelpOpen(false);
          }}
          showCloseButton
          wide
        >
          {helpStep === "what" ? (
            <>
              <div className="font-space text-3xl">{"What's a fact?"}</div>
              <div className="mt-4" />

              <div className="flex flex-col space-y-2">
                <div>
                  Oso Cloud makes a distinction between your {"application's"}{" "}
                  <strong>policy</strong> and its authorization{" "}
                  <strong>data</strong>.
                </div>
                <div>
                  A <strong>policy</strong> is a set of abstract rules that
                  descibe authorization logic, such as:
                </div>
                <div className="pl-2 py-1 border-l-4">
                  A user who has the admin role on a repository can edit it.
                </div>
                <div>
                  But authorization <strong>data</strong> is concrete
                  information about specific actors and resources in your
                  system, such as:
                </div>
                <div className="pl-2 py-1 border-l-4">
                  Alice has the admin role on the GitCloud repository.
                </div>
                <div>
                  A single piece of authorization data is called a{" "}
                  <strong>fact</strong>.
                </div>
                <div>
                  Oso Cloud makes authorization decisions by drawing inferences
                  from your facts via your policy.
                </div>
              </div>

              <div className="flex flex-row mt-4">
                <div className="flex-grow" />
                <Button
                  medium
                  icon={<HiChevronRight />}
                  onClick={() => setHelpStep("how")}
                >
                  How do I get facts into Oso Cloud?
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="font-space text-3xl">
                How do I get facts into Oso Cloud?
              </div>
              <div className="mt-4" />

              <div className="flex flex-col space-y-2">
                <div>
                  When you integrate your application with Oso Cloud, {"you'll"}{" "}
                  use the{" "}
                  <a
                    href="/install"
                    target="_blank"
                    className="underline text-blue-600"
                  >
                    Oso Cloud SDK
                  </a>{" "}
                  to persist facts programmatically as things happen in your
                  application.
                </div>

                <div>
                  Alternatively, you can send facts as{" "}
                  <a
                    href="https://www.osohq.com/docs/concepts/oso-cloud-data-model#sending-context-facts-at-authorization-time"
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-blue-600"
                  >
                    context at authorization time,
                  </a>{" "}
                  rather than persisting them.
                </div>

                <div>
                  If {"you're"} not ready to integrate your application quite
                  yet, you can also add individual facts by clicking the{" "}
                  {'"Add"'} button on any of the fact types on this page.
                </div>
                <div>
                  For more information about facts, be sure to{" "}
                  <a
                    href="https://www.osohq.com/docs/concepts/oso-cloud-data-model"
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-blue-600"
                  >
                    check out our documentation.
                  </a>
                </div>
              </div>

              <div className="flex flex-row mt-4">
                <Button
                  medium
                  icon={<HiChevronLeft />}
                  onClick={() => setHelpStep("what")}
                >
                  {"What's a fact?"}
                </Button>
              </div>
            </>
          )}
        </Modal>
      </WithSidebar>
    </>
  );
};

export default FactsPage;
